import { useCallback, useEffect, useRef, useState } from 'react';
import { browserEvent, type AnalyticsEvent } from '../../core/events';
import { moveDown, moveUp } from '../../core/profile';
import type { AwardInput, CertificationInput, JobInput, SectionName, Visibility } from '../../core/types';
import { call, type GetToken, type OwnerProfile, type VisitorProfile } from '../api';
import { linkTo, profileHref } from '../navigation';
import { AwardList } from './AwardList';
import { CertificationList } from './CertificationList';
import type { EntryOps } from './EntryForm';
import { IdentityColumn } from './IdentityColumn';
import { JobList } from './JobList';
import { ShareLink } from './ShareLink';
import { TestingTools } from './TestingTools';
import { VisibilityControl } from './VisibilityControl';

/**
 * One page for both audiences, laid out as the Product Contract draws it: the identity column
 * (photo, name, title, certifications) and the main area (jobs, awards). The owner's render
 * adds editing, visibility, the link, and "View as visitor"; the visitor's carries only what
 * `visitorPayload` allowed through (KTD4).
 */
type OwnerProps = {
  mode: 'owner';
  initial: OwnerProfile;
  getToken: GetToken;
  /** The verify switch (R16), from `/api/me`. */
  verify: boolean;
  onTrack(event: AnalyticsEvent): void;
  /** Where links point; the page's own origin outside tests. */
  origin?: string;
  /**
   * The 240 account (Clerk), where the name and photo are changed. `version` moves when the
   * account does; the page then reloads the profile, which syncs them. Absent without Clerk.
   */
  account?: { open(): void; version: number };
  /** From `/api/me`: shows the staff testing tools. The Worker refuses them to anyone else. */
  staff?: boolean;
};
type VisitorProps = { mode: 'visitor'; profile: VisitorProfile; getToken: GetToken };

const byPosition = <T extends { position: number }>(entries: readonly T[]) => [...entries].sort((a, b) => a.position - b.position);

export function ProfilePage(props: OwnerProps | VisitorProps) {
  return props.mode === 'owner' ? <OwnerView {...props} /> : <VisitorView {...props} />;
}

function VisitorView({ profile }: VisitorProps) {
  return <div className="profile-layout">
    <IdentityColumn displayName={profile.profile.displayName} title={profile.profile.title} photoUrl={profile.profile.photoUrl}>
      <CertificationList mode="visitor" certifications={profile.certifications} />
    </IdentityColumn>
    <div className="profile-main">
      <JobList jobs={profile.jobs.map((job, i) => ({ ...job, id: `j${i}` }))} />
      <AwardList awards={profile.awards.map((award, i) => ({ ...award, id: `a${i}` }))} currentYear={new Date().getFullYear()} />
    </div>
  </div>;
}

function OwnerView({ initial, getToken, verify, onTrack, origin = window.location.origin, account, staff = false }: OwnerProps) {
  const [data, setData] = useState(initial);
  const { profile } = data;

  /** Every owner route answers with the whole profile, so the page redraws from the reply. */
  const send = useCallback(async (path: string, init: RequestInit) => {
    const next = await call<OwnerProfile>(getToken, path, init);
    setData({ profile: next.profile, certifications: next.certifications, jobs: next.jobs, awards: next.awards, complete: next.complete });
  }, [getToken]);

  // The account changed (a name or photo saved in Clerk's dialog): reading the profile again
  // makes the Worker sync it. Not on the first render, which already has a fresh profile.
  const accountVersion = account?.version;
  const seenVersion = useRef(accountVersion);
  useEffect(() => {
    if (accountVersion === seenVersion.current) return;
    seenVersion.current = accountVersion;
    void send('/api/me/profile', { method: 'GET' }).catch(() => { /* The stored name stands until the next load. */ });
  }, [accountVersion, send]);

  const opsFor = <Body,>(section: SectionName): EntryOps<Body> => ({
    add: (body) => send(`/api/me/${section}`, { method: 'POST', body: JSON.stringify(body) }),
    edit: (id, body) => send(`/api/me/${section}/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
    remove: (id) => send(`/api/me/${section}/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    move: (id, delta) => {
      const entries = byPosition<OwnerProfile[SectionName][number]>(data[section]);
      const i = entries.findIndex((entry) => entry.id === id);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= entries.length) return Promise.resolve();
      const order = (delta < 0 ? moveUp : moveDown)(entries, id).map((entry) => entry.id);
      return send(`/api/me/${section}/order`, { method: 'PUT', body: JSON.stringify({ order }) });
    },
  });

  const link = `${origin}${profileHref(profile.slug)}`;
  const visitorLink = linkTo(profileHref(profile.slug), () => onTrack(browserEvent('preview_opened', { visibility: profile.visibility })));
  return <>
    <section className="owner-bar" aria-label="Sharing">
      <VisibilityControl value={profile.visibility} onChange={(visibility: Visibility) => send('/api/me/profile/visibility', { method: 'PUT', body: JSON.stringify({ visibility }) })} />
      <div className="owner-bar-share">
        <ShareLink url={link} visibility={profile.visibility} onCopied={() => onTrack(browserEvent('link_copied', { visibility: profile.visibility }))} />
        <a className="view-as-visitor" {...visitorLink}>View as visitor</a>
      </div>
    </section>
    <div className="profile-layout">
      <IdentityColumn displayName={profile.displayName} title={profile.title} photoUrl={profile.photoUrl} owner={{
        onSaveTitle: (title) => send('/api/me/profile', { method: 'PATCH', body: JSON.stringify({ title }) }),
        ...(account ? { onOpenAccount: account.open } : {}),
      }}>
        <CertificationList mode="owner" certifications={byPosition(data.certifications)} ops={opsFor<CertificationInput>('certifications')}
          displayName={profile.displayName} verifyEnabled={verify}
          onVerify={(id) => send(`/api/me/certifications/${encodeURIComponent(id)}/verify`, { method: 'POST', signal: AbortSignal.timeout(35_000) })} />
      </IdentityColumn>
      <div className="profile-main">
        <JobList jobs={byPosition(data.jobs)} ops={opsFor<JobInput>('jobs')} />
        <AwardList awards={byPosition(data.awards)} ops={opsFor<AwardInput>('awards')} currentYear={new Date().getFullYear()} />
      </div>
    </div>
    {staff && <TestingTools
      onFill={() => send('/api/me/profile/sample', { method: 'POST' })}
      onClear={() => send('/api/me/profile/clear', { method: 'POST' })} />}
  </>;
}
